import {FC, useState, useEffect, useRef, useCallback} from "react";
import {makeStyles} from "tss-react/mui";
import {
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
	Alert,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Chip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {Link, useNavigate} from "react-router-dom";
import {useQueryClient} from "@tanstack/react-query";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {useImproveAxios} from "@/app2/shared/hooks/useImproveAxios";
import type {BrightcoveApiConfig} from "@/app2/shared/data/repos/brightcove";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import {
	useGetLesson,
	useCreateLesson,
	useUpdateLesson,
	useDeleteLesson,
	useUploadLessonFile,
	LESSON_QUERY_KEYS,
	type LessonResponse,
	type PostLessonBody,
	type PutLessonBody,
	type LessonFileDto
} from "@/app2/shared/data";
import {AdminHeader} from "@/pages/ManagerView/ContentCreation/AdminHeader";
import {AdminInputText} from "@/pages/ManagerView/ContentCreation/AdminInputText";
import {AdminFieldContainer} from "@/pages/ManagerView/ContentCreation/AdminFieldContainer";
import {AttachQuizInline} from "@/app2/components/AttachQuizInline";
import {
	UploadBrightcoveVideo,
	BRIGHTCOVE_UPLOAD_VIDEO_FORMATS_HELPER,
	completeBrightcoveIngest,
	type PendingBrightcoveComplete,
	type VideoUploadType
} from "@/app2/components/UploadBrightcoveVideo";
import {AttachLinks} from "@/pages/ManagerView/ContentCreation/AttachLinks";
import {AttachFiles} from "@/pages/ManagerView/ContentCreation/AttachFiles";
import Editor from "@/components/admin/WYSIWYG/Editor";
import {AdminInputSwitch} from "@/pages/ManagerView/ContentCreation/AdminInputSwitch";
import ImageSelector from "@/pages/ManagerView/ContentCreation/ImageSelector";
import {IDropzoneFile} from "@buildwitt/shapeshift-common/v1/fileDropzone/fileDropzoneFile";
import {IFile} from "@buildwitt/shapeshift-types";
import {normalizeLessonLinkUrl} from "@/utils/common-utilities";

const LESSON_TITLE_MAX_CHARS = 150;
const LESSON_DESCRIPTION_MAX_CHARS = 500;

const useStyles = makeStyles()((theme) => ({
	root: {
		minHeight: "100vh",
		paddingBottom: theme.spacing(4)
	},
	formContainer: {
		maxWidth: "980px",
		margin: "0 auto",
		padding: theme.spacing(0, 3)
	},
	formFieldset: {
		borderRadius: "4px",
		"&.Mui-expanded": {
			margin: 0
		},
		"&.Mui-expanded:not(:first-of-type)": {
			marginTop: theme.spacing(2)
		},
		"&.Mui-expanded:last-of-type": {
			marginBottom: theme.spacing(2)
		},
		"&:not(:first-of-type)": {
			marginTop: theme.spacing(2)
		},
		"&:last-of-type": {
			marginBottom: theme.spacing(2)
		}
	},
	formFieldsetSummary: {
		padding: theme.spacing(0, 3),
		".MuiAccordionSummary-content": {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1)
		}
	},
	subtitle: {
		opacity: 0.7
	},
	formFieldsetDetails: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(4),
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3),
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(3)
	},
	formFieldsetNumber: {
		lineHeight: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		height: "24px",
		marginRight: theme.spacing(2),
		color: theme.palette.primary.contrastText,
		borderRadius: "100%",
		aspectRatio: "1 / 1",
		backgroundColor: theme.palette.primary.main
	},
	actions: {
		position: "sticky",
		bottom: 0,
		marginTop: theme.spacing(3),
		backgroundColor: theme.palette.background.paper,
		borderTop: `1px solid ${theme.palette.grey[200]}`,
		padding: theme.spacing(2, 3),
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		flexWrap: "wrap",
		gap: theme.spacing(2),
		width: "100%",
		boxSizing: "border-box",
		zIndex: 100,
		boxShadow: "0 -2px 8px rgba(0,0,0,0.1)"
	},
	actionsLeft: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		flexWrap: "wrap",
		gap: theme.spacing(2)
	},
	saveDraftBtn: {
		lineHeight: 1,
		color: theme.palette.primary.main,
		"&:hover": {
			color: theme.palette.primary.contrastText
		}
	},
	cancelBtn: {
		border: "none"
	},
	loadingContainer: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		minHeight: "400px"
	},
	/** Video field + upload button: avoid squeezing the text field when errors wrap. */
	videoUploadLayout: {
		flexDirection: "column",
		alignItems: "stretch",
		width: "100%",
		gap: theme.spacing(2),
		[theme.breakpoints.up("sm")]: {
			flexDirection: "row",
			flexWrap: "wrap",
			alignItems: "flex-start"
		}
	},
	videoUploadField: {
		width: "100%",
		minWidth: 0,
		[theme.breakpoints.up("sm")]: {
			flex: "1 1 280px",
			minWidth: 280,
			maxWidth: "100%"
		}
	},
	videoUploadActions: {
		flexShrink: 0,
		width: "100%",
		[theme.breakpoints.up("sm")]: {
			width: "auto"
		}
	},
	uploadBtn: {
		height: "56px",
		color: theme.palette.primary.main,
		"&:hover": { backgroundColor: "transparent" },
		"&.Mui-disabled": { opacity: 0.38 },
		flexShrink: 0
	},
	videoUrlChip: {
		border: `1px solid ${theme.palette.primary.main}`,
		"& .MuiChip-deleteIcon": { "&, &:hover": { color: theme.palette.primary.main } }
	},
	uploadInput: { width: "100%" },
	uploadInputChip: { "& input": { opacity: 0 } },
	/** Helper / counter text — higher contrast than default FormHelperText. */
	brighterFormHelperText: {
		color:
			theme.palette.mode === "dark"
				? theme.palette.grey[400]
				: theme.palette.grey[800]
	},
	title: {
		fontSize: "34px",
		lineHeight: 1.24
	},
	titleContainer: {
		display: "flex",
		flexDirection: "row",
		gap: theme.spacing(1),
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(4),
		marginLeft: theme.spacing(3)
	}
}));

/** File attached to lesson (from API or new upload). */
type LessonFormFile =
	| LessonFileDto  // Existing file from API
	| IDropzoneFile  // New file upload with File object
	| { id: number; name?: string; url?: string }; // Simplified structure

interface FormState {
	title: string;
	subtitle: string;
	content: string;
	videoUrl: string;
	videoType: string;
	videoThumbnailUrl: string;
	quizId: number | null;
	links: string[];
	files: LessonFormFile[];
}

const defaultForm: FormState = {
	title: "",
	subtitle: "",
	content: "",
	videoUrl: "",
	videoType: "url",
	videoThumbnailUrl: "",
	quizId: null,
	links: [],
	files: []
};

type SaveIntent =
	| "create-draft"
	| "create"
	| "edit-draft"
	| "edit-publish"
	| "edit-unpublish";

function getLessonSaveSuccessMessage(intent: SaveIntent): string {
	switch (intent) {
		case "create":
		case "edit-publish":
			return "Lesson saved and published successfully";
		case "edit-unpublish":
			return "Lesson unpublished successfully";
		case "create-draft":
		case "edit-draft":
			return "Lesson saved successfully";
	}
}

export interface AdminLessonPageContentProps {
	lessonId?: number;
	onSaveResponse: (data: LessonResponse) => void;
	onDeleteResponse: () => void;
	onGetResponse: (data: LessonResponse | null, statusCode?: number) => void;
	/** When set (e.g. modal), invoked instead of navigating to /admin/lessons on Cancel */
	onCancel?: () => void;
	type?: "fullscreen" | "modal";
}

export const AdminLessonPageContent: FC<AdminLessonPageContentProps> = ({
	lessonId,
	onSaveResponse,
	onDeleteResponse,
	onGetResponse,
	onCancel,
	type = "fullscreen"
}) => {
	const {classes, cx} = useStyles();
	const navigate = useNavigate();
	const isEdit = !!lessonId;
	const queryClient = useQueryClient();
	const apiConfig = useImproveAxios() as BrightcoveApiConfig;
	const {successSnackbar, errorSnackbar} = useCustomSnackbar();

	/** Deferred Brightcove complete after first save (create flow); flushed via {@link completeBrightcoveIngest}. */
	const pendingBrightcoveCompleteRef = useRef<{videoId: string; videoUrl: string} | null>(null);

	const [form, setForm] = useState<FormState>(defaultForm);
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
	const [uploadState, setUploadState] = useState<VideoUploadType>("idle");
	const [activeSaveIntent, setActiveSaveIntent] = useState<SaveIntent | null>(null);
	/** Toggle: use auto-generated/fallback video thumbnail (true) or custom thumbnail (false). Matches legacy Lesson Media. */
	const [enabledAutogen, setEnabledAutogen] = useState<boolean>(true);

	const {data: lesson, isLoading, error, isError} = useGetLesson(lessonId);
	const createMutation = useCreateLesson();
	const updateMutation = useUpdateLesson(lessonId ?? 0);
	const deleteMutation = useDeleteLesson();
	const uploadFileMutation = useUploadLessonFile();
	const uploadThumbnailMutation = useUploadLessonFile();
	const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);


	const flushPendingBrightcoveComplete = useCallback(
		async (newLessonId: number): Promise<boolean> => {
			const pending = pendingBrightcoveCompleteRef.current;
			if (!pending) return true;
			const res = await completeBrightcoveIngest(apiConfig, {
				entityType: "lesson",
				entityId: newLessonId,
				videoId: pending.videoId,
				videoUrl: pending.videoUrl
			});
			if (!res.success) {
				errorSnackbar("error" in res ? res.error : "Failed to attach video to lesson");
				return false;
			}
			pendingBrightcoveCompleteRef.current = null;
			await queryClient.invalidateQueries({
				queryKey: [LESSON_QUERY_KEYS.DETAIL, newLessonId]
			});
			return true;
		},
		[apiConfig, errorSnackbar, queryClient]
	);

	useEffect(() => {
		if (lesson) {

			setForm((prev) => ({
				...prev,
				title: lesson.title ?? "",
				subtitle: (lesson.subtitle ?? "").slice(0, LESSON_DESCRIPTION_MAX_CHARS),
				content: lesson.content ?? "",
				videoUrl: lesson.videoUrl ?? "",
				videoType: lesson.videoType ?? "url",
				videoThumbnailUrl: lesson.videoThumbnailUrl ?? "",
				quizId: lesson.quizId ?? null,
				links: lesson.links ?? [],
				files: lesson.files ?? []
			}));
			if (lesson.videoThumbnailUrl?.trim()) {
				setEnabledAutogen(false);
			}
		}
	}, [lesson]);

	useEffect(() => {
		if (!isEdit) {
			setForm(defaultForm);
			setValidationErrors({});
			setEnabledAutogen(true);
			setUploadState("idle");
			setThumbnailFile(null);
			pendingBrightcoveCompleteRef.current = null;
		}
	}, [isEdit]);

	useEffect(() => {
		if (isError && error && "response" in error) {
			const statusCode = (error as any).response?.status;
			onGetResponse(null, statusCode);
		} else if (lesson && !isEdit) {
			onGetResponse(lesson);
		}
	}, [isError, error, lesson, isEdit, onGetResponse]);

	const validate = (intent: SaveIntent): boolean => {
		const err: Record<string, string> = {};
		if (!form.title.trim()) err.title = "Title is required";
		if (!form.videoUrl.trim()) err.videoUrl = "Video is required";
		if (form.subtitle.length > LESSON_DESCRIPTION_MAX_CHARS) {
			err.subtitle = `Description must be ${LESSON_DESCRIPTION_MAX_CHARS} characters or fewer`;
		}
		if (form.links.some((link) => normalizeLessonLinkUrl(link) === null)) {
			err.links = "Each link must be a valid URL.";
		}
		setValidationErrors(err);
		if (Object.keys(err).length > 0) return false;
		if (
			(intent === "create" || intent === "edit-publish") &&
			disablePublish
		) {
			return false;
		}
		return true;
	};

	const isBrightcoveVideo =
		form.videoType === "brightcove" && !!form.videoUrl;
	/** Align with legacy AdminLessonPageContent: block publish until Brightcove has a thumbnail or autogen is on. */
	const disablePublish =
		isBrightcoveVideo &&
		!form.videoThumbnailUrl?.trim() &&
		!enabledAutogen;

	const handleSaveLesson = async (intent: SaveIntent) => {
		if (!validate(intent)) return;
		setActiveSaveIntent(intent);

		const existingFileIds: number[] = [];
		const newFiles: File[] = [];

		for (const file of form.files) {
			if ("id" in file && typeof file.id === "number" && file.id > 0) {
				existingFileIds.push(file.id);
			} else if ("file" in file && file.file instanceof File) {
				newFiles.push(file.file);
			}
		}

		const successMessage = getLessonSaveSuccessMessage(intent);

		try {
			// Upload pending thumbnail file before saving
			let resolvedThumbnailUrl: string | null | undefined = enabledAutogen
				? null
				: (form.videoThumbnailUrl || undefined);
			if (!enabledAutogen && thumbnailFile) {
				const thumbnailResult = await uploadThumbnailMutation.mutateAsync(thumbnailFile);
				const uploadedUrl = thumbnailResult.files?.[0]?.url;
				if (uploadedUrl) {
					resolvedThumbnailUrl = uploadedUrl;
					setForm((prev) => ({...prev, videoThumbnailUrl: uploadedUrl}));
					setThumbnailFile(null);
				}
			}

			const uploadedFileIds: number[] = [];
			for (const file of newFiles) {
				const result = await uploadFileMutation.mutateAsync(file);
				if (result.files && result.files.length > 0) {
					uploadedFileIds.push(result.files[0].id);
				}
			}

			const allFileIds = [...existingFileIds, ...uploadedFileIds];

			const basePayload = {
				title: form.title,
				subtitle: form.subtitle || undefined,
				content: form.content || undefined,
				videoUrl: form.videoUrl || undefined,
				videoType: form.videoType || undefined,
				videoThumbnailUrl: resolvedThumbnailUrl,
				quizId: form.quizId as any,
				links: form.links.length > 0 ? form.links : undefined,
				existingFileIds: allFileIds.length > 0 ? allFileIds : undefined
			};

			if (!isEdit) {
				const publishedAt =
					intent === "create" ? new Date().toISOString() : null;
				const data = await createMutation.mutateAsync({
					body: {
						...basePayload,
						publishedAt
					} as unknown as PostLessonBody
				});
				await flushPendingBrightcoveComplete(data.id);
				successSnackbar(successMessage);
				onSaveResponse(data);
				return;
			}

			let putBody: PutLessonBody;
			switch (intent) {
				case "edit-publish":
					putBody = {
						...basePayload,
						publishedAt: new Date().toISOString()
					} as unknown as PutLessonBody;
					break;
				case "edit-draft":
				case "edit-unpublish":
					putBody = {
						...basePayload,
						publishedAt: null
					} as unknown as PutLessonBody;
					break;
				default:
					putBody = {...basePayload} as unknown as PutLessonBody;
					break;
			}

			const data = await updateMutation.mutateAsync(putBody);
			successSnackbar(successMessage);
			onSaveResponse(data);
		} catch (e) {
			console.error("Save lesson failed", e);
			errorSnackbar("Failed to save lesson");
		} finally {
			setActiveSaveIntent(null);
		}
	};

	const handleUpload = (videoId: string, pending?: PendingBrightcoveComplete): void => {
		setForm((prev) => ({
			...prev,
			videoType: "brightcove",
			videoUrl: videoId
		}));
		if (pending?.pendingBrightcoveComplete) {
			pendingBrightcoveCompleteRef.current = {
				videoId,
				videoUrl: pending.videoUrl
			};
		} else {
			pendingBrightcoveCompleteRef.current = null;
		}
	};
	const handleClear = (): void => {
		setForm((prev) => ({ ...prev, videoType: "url", videoUrl: "" }));
		pendingBrightcoveCompleteRef.current = null;
	};

	const handleDelete = async () => {
		if (!lessonId) return;
		if (!window.confirm("Are you sure you want to delete this lesson?")) return;
		try {
			await deleteMutation.mutateAsync({lessonId});
			successSnackbar("Lesson deleted");
			onDeleteResponse();
		} catch (e) {
			console.error("Delete lesson failed", e);
		}
	};

	if (isEdit && isLoading) {
		return (
			<Box className={classes.root}>
				<Box className={classes.loadingContainer}>
					<CircularProgress />
				</Box>
			</Box>
		);
	}

	if (isEdit && error) {
		return (
			<Box className={classes.root}>
				<Box className={classes.formContainer}>
					<Alert severity="error">
						Failed to load lesson: {(error as Error).message}
					</Alert>
				</Box>
			</Box>
		);
	}

	const isSaving = createMutation.isPending || updateMutation.isPending || uploadThumbnailMutation.isPending;
	const isPublished = Boolean(isEdit && lesson?.publishedAt);

	const handleCancel = (): void => {
		if (onCancel) {
			onCancel();
			return;
		}
		navigate(isEdit && lessonId ? `/admin/lessons/${lessonId}` : "/admin/lessons");
	};

	return (
		<Box className={classes.root}>
			{type === "fullscreen" ? (
				<AdminHeader
					rootSlug="/admin/lessons"
					rootBreadcrumb={
						<>
							<PlayCircleFilledWhiteOutlinedIcon />
							<Link to="/admin/lessons">
								<Typography variant="body1">Lessons</Typography>
							</Link>
						</>
					}
					editMode={isEdit ? "edit" : "create"}
					entity={lesson as any}
					type="lesson"
					title={isEdit ? (lesson?.title ?? "") : "Create New Lesson"}
					subtitle={isEdit ? `Lesson ID: ${lessonId}` : "Create a new lesson"}
				/>
			) : (
				<Box className={classes.titleContainer}>
					<Typography variant="h4" className={classes.title}>
						{isEdit ? (lesson?.title ?? "") : "Create New Lesson"}
					</Typography>
				</Box>
			)}
			<Box className={classes.formContainer}>
				{(createMutation.isError || updateMutation.isError) && (
					<Alert severity="error" sx={{mb: 2}}>
						{(createMutation.error as Error)?.message ??
							(updateMutation.error as Error)?.message ??
							"An error occurred"}
					</Alert>
				)}

				<Accordion defaultExpanded className={classes.formFieldset}>
					<AccordionSummary className={classes.formFieldsetSummary} expandIcon={<ExpandMoreIcon />}>
						<Stack direction="row" alignItems="center">
							<span className={classes.formFieldsetNumber}>1</span>
							<Typography variant="h6" component="p">
								Lesson Information
							</Typography>
						</Stack>
						<Typography className={classes.subtitle}>
							Add the basic details for your lesson in English.
						</Typography>
					</AccordionSummary>
					<AccordionDetails className={classes.formFieldsetDetails}>
						<AdminInputText
							label="Lesson title"
							required
							value={form.title}
							onChange={(e) => {
								const title = e.target.value.slice(0, LESSON_TITLE_MAX_CHARS);
								setForm((prev) => ({...prev, title}));
								setValidationErrors((prev) =>
									prev.title ? {...prev, title: ""} : prev
								);
							}}
							error={validationErrors.title}
							helperText={
								validationErrors.title
									? undefined
									: `${form.title.length}/${LESSON_TITLE_MAX_CHARS}`
							}
							FormHelperTextProps={{className: classes.brighterFormHelperText}}
							inputProps={{maxLength: LESSON_TITLE_MAX_CHARS}}
						/>
						<AdminInputText
							label="Description"
							multiline
							rows={5}
							value={form.subtitle}
							onChange={(e) => {
								const subtitle = e.target.value.slice(0, LESSON_DESCRIPTION_MAX_CHARS);
								setForm((prev) => ({...prev, subtitle}));
								setValidationErrors((prev) =>
									prev.subtitle ? {...prev, subtitle: ""} : prev
								);
							}}
							error={validationErrors.subtitle}
							helperText={
								validationErrors.subtitle
									? undefined
									: `${form.subtitle.length}/${LESSON_DESCRIPTION_MAX_CHARS}`
							}
							FormHelperTextProps={{className: classes.brighterFormHelperText}}
							inputProps={{maxLength: LESSON_DESCRIPTION_MAX_CHARS}}
						/>
					</AccordionDetails>
				</Accordion>

				<Accordion className={classes.formFieldset}>
					<AccordionSummary className={classes.formFieldsetSummary} expandIcon={<ExpandMoreIcon />}>
						<Stack direction="row" alignItems="center">
							<span className={classes.formFieldsetNumber}>2</span>
							<Typography variant="h6" component="p">
								Lesson Media
							</Typography>
						</Stack>
						<Typography className={classes.subtitle}>
							Upload a video file or paste a URL. Video thumbnail optional.
						</Typography>
					</AccordionSummary>
					<AccordionDetails className={classes.formFieldsetDetails}>
						<UploadBrightcoveVideo
							lessonId={lessonId ?? 0}
							uploadState={uploadState}
							setUploadState={setUploadState}
							handleSuccess={handleUpload}
							errorPlacement="children"
						>
							{({onClick, progress, error}) => (
								<Stack className={classes.videoUploadLayout}>
									<Box className={classes.videoUploadField}>
										<AdminInputText
											className={cx(
												classes.uploadInput,
												isBrightcoveVideo && classes.uploadInputChip
											)}
											fullWidth
											label="Video"
											required
											helperText={
												validationErrors.videoUrl
													? undefined
													: `Upload a video file (${BRIGHTCOVE_UPLOAD_VIDEO_FORMATS_HELPER}) or paste a YouTube link (copy it from the browser address bar, not the "Share" button). Max file size: 10GB.`
											}
											FormHelperTextProps={{className: classes.brighterFormHelperText}}
											value={form.videoUrl}
											onChange={(e) => {
												setForm((prev) => ({
													...prev,
													videoUrl: e.target.value,
													videoType: "url"
												}));
												setValidationErrors((prev) =>
													prev.videoUrl ? {...prev, videoUrl: ""} : prev
												);
											}}
											error={validationErrors.videoUrl}
											InputProps={{
												readOnly: isBrightcoveVideo,
												startAdornment: isBrightcoveVideo ? (
													<Chip
														className={classes.videoUrlChip}
														label={form.videoUrl}
														onDelete={handleClear}
													/>
												) : null
											}}
										/>
									</Box>
									<Stack className={classes.videoUploadActions}>
										<Button
											variant="outlined"
											className={classes.uploadBtn}
											onClick={onClick}
											startIcon={<UploadFileOutlinedIcon />}
											disabled={
												(isBrightcoveVideo && uploadState === "idle") ||
												uploadState === "progress"
											}
										>
											{uploadState === "progress"
												? `Uploading ${Math.round(progress)}%`
												: "Upload file"}
										</Button>
									</Stack>
									{error}
								</Stack>
							)}
						</UploadBrightcoveVideo>
						<AdminInputSwitch
							label="Use the auto-generated or fallback video thumbnail"
							checked={enabledAutogen}
							onChange={(_e, checked) => {
								setEnabledAutogen(!!checked);
								if (checked) {
									setForm((prev) => ({ ...prev, videoThumbnailUrl: "" }));
									setThumbnailFile(null);
								}
							}}
						/>
						{!enabledAutogen && (
							<ImageSelector
								defaultImageUrl={form.videoThumbnailUrl || undefined}
								onImageSelected={(files) => {
									const file = files?.[0]?.file;
									const preview = files?.[0]?.preview;
									if (file && preview) {
										setThumbnailFile(file);
										setForm((prev) => ({...prev, videoThumbnailUrl: preview}));
									}
								}}
								label="Thumbnail"
							/>
						)}
					</AccordionDetails>
				</Accordion>

				<Accordion className={classes.formFieldset}>
					<AccordionSummary className={classes.formFieldsetSummary} expandIcon={<ExpandMoreIcon />}>
						<Stack direction="row" alignItems="center">
							<span className={classes.formFieldsetNumber}>3</span>
							<Typography variant="h6" component="p">
								Include Quiz (Optional)
							</Typography>
						</Stack>
						<Typography className={classes.subtitle}>
							Include quizzes to support learning.
						</Typography>
					</AccordionSummary>
					<AccordionDetails className={classes.formFieldsetDetails}>
						<AttachQuizInline
							quizId={form.quizId ?? undefined}
							onChange={(id) =>
								setForm((prev) => ({ ...prev, quizId: id ?? null })) // quizzes
							}
						/>
					</AccordionDetails>
				</Accordion>

				<Accordion className={classes.formFieldset}>
					<AccordionSummary className={classes.formFieldsetSummary} expandIcon={<ExpandMoreIcon />}>
						<Stack direction="row" alignItems="center">
							<span className={classes.formFieldsetNumber}>4</span>
							<Typography variant="h6" component="p">
								Include Links (Optional)
							</Typography>
						</Stack>
						<Typography className={classes.subtitle}>
							Include links to support learning.
						</Typography>
					</AccordionSummary>
					<AccordionDetails className={classes.formFieldsetDetails}>
						<AttachLinks
							links={form.links}
							onChange={(links) => {
								setForm((prev) => ({ ...prev, links }));
								setValidationErrors((prev) =>
									prev.links ? { ...prev, links: "" } : prev
								);
							}}
							error={validationErrors.links}
							onLinkInputChange={() =>
								setValidationErrors((prev) =>
									prev.links ? { ...prev, links: "" } : prev
								)
							}
						/>
					</AccordionDetails>
				</Accordion>

				<Accordion className={classes.formFieldset}>
					<AccordionSummary className={classes.formFieldsetSummary} expandIcon={<ExpandMoreIcon />}>
						<Stack direction="row" alignItems="center">
							<span className={classes.formFieldsetNumber}>5</span>
							<Typography variant="h6" component="p">
								Include Files (Optional)
							</Typography>
						</Stack>
						<Typography className={classes.subtitle}>
							Attach files to support learning.
						</Typography>
					</AccordionSummary>
					<AccordionDetails className={classes.formFieldsetDetails}>
						<AttachFiles
							value={form.files as (IFile | IDropzoneFile)[]}
							onChange={(files) =>
								setForm((prev) => ({
									...prev,
									files: files as LessonFormFile[]
								}))
							}
						/>
					</AccordionDetails>
				</Accordion>

				<Box className={classes.actions}>
					<Box className={classes.actionsLeft}>
					<Button
						variant="contained"
						color="primary"
						onClick={() => {
							if (!isEdit) {
								handleSaveLesson("create");
							} else if (isPublished) {
								handleSaveLesson("edit-unpublish");
							} else {
								handleSaveLesson("edit-publish");
							}
						}}
						disabled={isSaving || (!isPublished && disablePublish)}
					>
						{isSaving && activeSaveIntent && !activeSaveIntent.includes("draft") ? (
							<CircularProgress size={20} color="inherit" />
						) : isEdit && isPublished ? (
							"Unpublish"
						) : (
							"Publish"
						)}
					</Button>
					<Button
						variant="outlined"
						className={classes.saveDraftBtn}
						onClick={() =>
							handleSaveLesson(!isEdit ? "create-draft" : "edit-draft")
						}
						disabled={isSaving}
					>
						{isSaving && activeSaveIntent?.includes("draft") ? <CircularProgress size={20} /> : "Save as draft"}
					</Button>
					</Box>
					<Button className={classes.cancelBtn} variant="text" onClick={handleCancel} disabled={isSaving}>
						Cancel
					</Button>
				</Box>
			</Box>
		</Box>
	);
};
